import React, { useState, useContext, useMemo } from "react";
import {
  Box,
  Button,
  Typography,
  Switch,
  FormControlLabel,
  Paper,
  Chip,
  Divider,
  Alert,
  Stack,
  Tooltip
} from "@mui/material";
import { useParams } from "react-router-dom";
import { AuthContext } from "../../../../helpers/AuthContext";

function statChip(label, value, color = "default") {
  return (
    <Chip
      key={label}
      label={`${label}: ${Number(value || 0)}`}
      color={color}
      size="small"
      sx={{ mr: 1, mb: 1 }}
    />
  );
}

function SimpleTable({ rows = [], max = 10 }) {
  if (!rows?.length) return <Typography variant="body2">No rows.</Typography>;
  const slice = rows.slice(0, max);
  const columns = Object.keys(slice[0] || {});
  return (
    <Box sx={{ overflow: "auto", border: "1px solid #eee", borderRadius: 1 }}>
      <table style={{ borderCollapse: "collapse", width: "100%" }}>
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c} style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #eee", background: "#fafafa" }}>
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {slice.map((r, i) => (
            <tr key={i}>
              {columns.map((c) => (
                <td key={c} style={{ padding: "8px", borderBottom: "1px solid #f2f2f2" }}>
                  {typeof r[c] === "object" ? JSON.stringify(r[c]) : String(r[c] ?? "")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </Box>
  );
}

function toCsv(rows) {
  if (!rows?.length) return "";
  const headers = Object.keys(rows[0]);
  const esc = (v) => {
    const s = v == null ? "" : String(v);
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const lines = [
    headers.join(","),
    ...rows.map((r) => headers.map((h) => esc(typeof r[h] === "object" ? JSON.stringify(r[h]) : r[h])).join(","))
  ];
  return lines.join("\n");
}

export default function ThirdPartyImport() {
  const { instanceUniqueId } = useParams();
  const { authState } = useContext(AuthContext);
  const [file, setFile] = useState(null);
  const [dryRun, setDryRun] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const SERVER = process.env.REACT_APP_NODEJS_SERVER_URL;
  const canUse = ["finance-rep", "administrator"].includes(String(authState?.role || "").toLowerCase());

  const runImport = async () => {
    if (!file) return alert("Choose a CSV/XML file first.");
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("instanceUniqueId", instanceUniqueId);
      fd.append("dryRun", String(dryRun));

      const res = await fetch(`${SERVER}/invoices/import-third-party`, {
        method: "POST",
        headers: { accessToken: localStorage.getItem("accessToken") },
        body: fd
      });
      const json = await res.json();

      // Normalize shapes:
      // A) { ok, summary: { customers:{...}, invoices:{...} }, samples:{...}, errors:[] }
      // B) { customers:{...}, invoices:{...}, errors:[] }
      const normalized = {
        ok: json.ok ?? true,
        summary: json.summary ?? {
          customers: json.customers ?? { created: 0, updated: 0, matched: 0, skipped: 0 },
          invoices: json.invoices ?? { created: 0, updated: 0, skipped: 0 }
        },
        samples: json.samples ?? {},
        errors: json.errors ?? []
      };

      if (!normalized.ok) throw new Error(json.error || "Import failed");
      setResult(normalized);
    } catch (e) {
      console.error(e);
      alert(e.message || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const planCSVs = useMemo(() => {
    if (!result?.samples) return null;
    const out = {};
    const s = result.samples;

    // Prepare CSVs if sample arrays exist
    if (s.customers?.created?.length) out["customers-created.csv"] = toCsv(s.customers.created);
    if (s.customers?.updated?.length) out["customers-updated.csv"] = toCsv(s.customers.updated);
    if (s.customers?.matched?.length) out["customers-matched.csv"] = toCsv(s.customers.matched);
    if (s.customers?.skipped?.length) out["customers-skipped.csv"] = toCsv(s.customers.skipped);

    if (s.invoices?.created?.length) out["invoices-created.csv"] = toCsv(s.invoices.created);
    if (s.invoices?.updated?.length) out["invoices-updated.csv"] = toCsv(s.invoices.updated);
    if (s.invoices?.skipped?.length) out["invoices-skipped.csv"] = toCsv(s.invoices.skipped);

    return out;
  }, [result]);

  const downloadCsv = (name, csv) => {
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  if (!canUse) {
    return <Typography variant="body1" sx={{ p: 2 }}>You don’t have permission to use Imports.</Typography>;
  }

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>Third-party Import (Commonsku CSV/XML)</Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Upload Commonsku customers/invoices export. Start with <strong>Dry Run</strong>; if it looks good, uncheck and apply.  
        The importer will map customers, create missing ones, and stage invoices. On real run, it writes to the DB.
      </Typography>

      <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2, flexWrap: "wrap" }}>
        <input
          type="file"
          accept=".csv,.xml,text/csv,application/xml,text/xml"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
        />
        <FormControlLabel
          control={<Switch checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} />}
          label="Dry Run"
        />
        <Button variant="contained" onClick={runImport} disabled={busy || !file}>
          {busy ? "Working..." : dryRun ? "Preview Import" : "Apply Import"}
        </Button>
      </Box>

      {result && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>Summary</Typography>

          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Customers</Typography>
            <Stack direction="row" flexWrap="wrap">
              {statChip("Created", result.summary.customers?.created, "success")}
              {statChip("Updated", result.summary.customers?.updated, "info")}
              {statChip("Matched", result.summary.customers?.matched, "default")}
              {statChip("Skipped", result.summary.customers?.skipped, "warning")}
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Invoices</Typography>
            <Stack direction="row" flexWrap="wrap">
              {statChip("Created", result.summary.invoices?.created, "success")}
              {statChip("Updated", result.summary.invoices?.updated, "info")}
              {statChip("Skipped", result.summary.invoices?.skipped, "warning")}
            </Stack>
          </Paper>

          {!!result.errors?.length && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Errors</Typography>
              {result.errors.map((e, i) => (
                <Alert key={i} severity="error" sx={{ mb: 1 }}>
                  {typeof e === "string" ? e : JSON.stringify(e)}
                </Alert>
              ))}
            </Paper>
          )}

          {/* Optional previews if backend provides sample rows */}
          {result.samples?.customers && (
            <>
              <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>Preview — Customers (first 10)</Typography>
              <Divider sx={{ mb: 2 }} />
              {["created", "updated", "matched", "skipped"].map((k) =>
                result.samples.customers[k]?.length ? (
                  <Box key={k} sx={{ mb: 2 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 600, textTransform: "capitalize" }}>
                        {k} ({result.samples.customers[k].length})
                      </Typography>
                      {planCSVs?.[`customers-${k}.csv`] && (
                        <Tooltip title="Download CSV plan for this set">
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() =>
                              downloadCsv(`customers-${k}.csv`, planCSVs[`customers-${k}.csv`])
                            }
                          >
                            Download CSV
                          </Button>
                        </Tooltip>
                      )}
                    </Stack>
                    <SimpleTable rows={result.samples.customers[k]} />
                  </Box>
                ) : null
              )}
            </>
          )}

          {result.samples?.invoices && (
            <>
              <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>Preview — Invoices (first 10)</Typography>
              <Divider sx={{ mb: 2 }} />
              {["created", "updated", "skipped"].map((k) =>
                result.samples.invoices[k]?.length ? (
                  <Box key={k} sx={{ mb: 2 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 600, textTransform: "capitalize" }}>
                        {k} ({result.samples.invoices[k].length})
                      </Typography>
                      {planCSVs?.[`invoices-${k}.csv`] && (
                        <Tooltip title="Download CSV plan for this set">
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() =>
                              downloadCsv(`invoices-${k}.csv`, planCSVs[`invoices-${k}.csv`])
                            }
                          >
                            Download CSV
                          </Button>
                        </Tooltip>
                      )}
                    </Stack>
                    <SimpleTable rows={result.samples.invoices[k]} />
                  </Box>
                ) : null
              )}
            </>
          )}
        </Box>
      )}
    </Paper>
  );
}
